import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/buchhaltung`;
const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const de = (s) => (s && s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s || "–");
const JAHR = new Date().getFullYear();

const laden = async (pfad, params, name) => {
  const res = await axios.get(`${API}/${pfad}`, { params, responseType: "blob" }).catch(() => null);
  if (!res) return toast.error("Export fehlgeschlagen");
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
};

const Export = ({ pfad, params, name, id }) => (
  <div className="flex gap-2">
    <Button variant="outline" onClick={() => laden(pfad, { ...params, format: "pdf" }, `${name}.pdf`)} data-testid={`${id}-pdf-btn`}><FileDown size={16} className="mr-2" />PDF</Button>
    <Button variant="outline" onClick={() => laden(pfad, { ...params, format: "csv" }, `${name}.csv`)} data-testid={`${id}-csv-btn`}><FileSpreadsheet size={16} className="mr-2" />CSV / Excel</Button>
  </div>
);

const Kachel = ({ label, wert, id }) => (
  <div className="rounded-xl border border-stone-200 bg-white px-4 py-3" data-testid={id}>
    <div className="text-xs uppercase tracking-wider text-stone-500">{label}</div>
    <div className="text-lg font-semibold tabular-nums text-stone-900">{wert}</div>
  </div>
);

const Nummernkreise = ({ kreise }) => (
  <div className="mb-4 space-y-1" data-testid="nummernkreise">
    {kreise.map((k) => (
      <div key={k.kreis} className={`flex items-center gap-2 text-sm ${k.fehlend.length ? "text-amber-700" : "text-emerald-700"}`} data-testid={`nummernkreis-${k.kreis}`}>
        {k.fehlend.length ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
        Nummernkreis {k.kreis}: {k.von} bis {k.bis} ({k.anzahl} {k.anzahl === 1 ? "Rechnung" : "Rechnungen"}) – {k.fehlend.length ? `Lücken: ${k.fehlend.join(", ")}` : "lückenlos"}
      </div>
    ))}
  </div>
);

const Tabelle = ({ kopf, children, id }) => (
  <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
    <table className="w-full text-sm" data-testid={id}>
      <thead className="bg-stone-100 text-left text-xs uppercase tracking-wider text-stone-600">
        <tr>{kopf.map((k) => <th key={k} className="px-3 py-2 whitespace-nowrap">{k}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Ausgangsbuch = () => {
  const [jahr, setJahr] = useState(JAHR);
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/ausgangsbuch`, { params: { jahr } }).then((r) => setD(r.data)).catch(() => toast.error("Ausgangsbuch konnte nicht geladen werden")); }, [jahr]);
  return (
    <div data-testid="ausgangsbuch">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <label className="text-sm text-stone-600">Geschäftsjahr{" "}
          <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))} className="ml-2 rounded border border-stone-300 bg-white px-2 py-1" data-testid="ausgangsbuch-jahr">
            {[JAHR + 1, JAHR, JAHR - 1, JAHR - 2, JAHR - 3].map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </label>
        <Export pfad="ausgangsbuch/export" params={{ jahr }} name={`Rechnungsausgangsbuch_${jahr}`} id="ausgangsbuch" />
      </div>
      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Kachel label="Rechnungen" wert={d.summen.anzahl} id="ab-anzahl" />
            <Kachel label="Umsatz nach Stornos" wert={eur(d.summen.netto_umsatz)} id="ab-umsatz" />
            <Kachel label="Bezahlt" wert={eur(d.summen.bezahlt)} id="ab-bezahlt" />
            <Kachel label="Offen" wert={eur(d.summen.offen)} id="ab-offen" />
          </div>
          <Nummernkreise kreise={d.nummernkreise} />
          <Tabelle id="ausgangsbuch-tabelle" kopf={["Nr.", "Datum", "Art", "Empfänger", "Leistung", "Betrag", "Bezahlt", "Offen", "Status"]}>
            {d.zeilen.map((z) => (
              <tr key={z.id} className={`border-t border-stone-100 ${z.betrag < 0 ? "text-red-700" : ""}`} data-testid={`ab-zeile-${z.rechnungsnummer}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{z.rechnungsnummer}</td><td className="px-3 py-2">{de(z.datum)}</td>
                <td className="px-3 py-2">{z.art}{z.bezug && <span className="text-stone-500"> zu {z.bezug}</span>}</td>
                <td className="px-3 py-2">{z.empfaenger}</td><td className="px-3 py-2 max-w-xs truncate" title={z.leistung}>{z.leistung}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(z.betrag)}</td><td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(z.bezahlt)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{eur(z.offen)}</td><td className="px-3 py-2">{z.status}</td>
              </tr>
            ))}
          </Tabelle>
          <p className="mt-3 text-xs text-stone-500">Bildungsleistungen umsatzsteuerfrei nach § 4 Nr. 21 UStG. Stornorechnungen erscheinen mit negativem Betrag.</p>
        </>
      )}
    </div>
  );
};

const Belegjournal = () => {
  const [von, setVon] = useState(`${JAHR}-01-01`);
  const [bis, setBis] = useState(`${JAHR}-12-31`);
  const [d, setD] = useState(null);
  const holen = useCallback(() => axios.get(`${API}/belegjournal`, { params: { von, bis } }).then((r) => setD(r.data)).catch(() => toast.error("Belegjournal konnte nicht geladen werden")), [von, bis]);
  useEffect(() => { holen(); }, [holen]);
  return (
    <div data-testid="belegjournal">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3 text-sm text-stone-600">
          <label>Von <input type="date" value={von} onChange={(e) => setVon(e.target.value)} lang="de" className="ml-1 rounded border border-stone-300 px-2 py-1" data-testid="journal-von" /></label>
          <label>Bis <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} lang="de" className="ml-1 rounded border border-stone-300 px-2 py-1" data-testid="journal-bis" /></label>
        </div>
        <Export pfad="belegjournal/export" params={{ von, bis }} name={`Belegjournal_${von}_${bis}`} id="journal" />
      </div>
      {d && (
        <>
          <div className="flex flex-wrap gap-3 mb-4" data-testid="journal-summen">
            {Object.entries(d.summen).map(([k, v]) => <Kachel key={k} label={k} wert={eur(v)} id={`journal-summe-${k}`} />)}
            <Kachel label="Saldo" wert={eur(d.saldo)} id="journal-saldo" />
          </div>
          <Tabelle id="journal-tabelle" kopf={["Lfd.", "Datum", "Belegart", "Beleg-Nr.", "Partner", "Betrag", "Gegenkonto", "Text"]}>
            {d.zeilen.map((z) => (
              <tr key={z.lfd} className="border-t border-stone-100" data-testid={`journal-zeile-${z.lfd}`}>
                <td className="px-3 py-2 tabular-nums text-stone-500">{z.lfd}</td><td className="px-3 py-2">{de(z.datum)}</td><td className="px-3 py-2">{z.belegart}</td>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{z.belegnummer}</td><td className="px-3 py-2">{z.partner}</td>
                <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${z.betrag < 0 ? "text-red-700" : ""}`}>{eur(z.betrag)}</td>
                <td className="px-3 py-2">{z.gegenkonto}</td><td className="px-3 py-2 max-w-xs truncate" title={z.text}>{z.text}</td>
              </tr>
            ))}
          </Tabelle>
          {d.zeilen.length === 0 && <p className="mt-3 text-sm text-stone-500" data-testid="journal-leer">Keine Belege in diesem Zeitraum.</p>}
        </>
      )}
    </div>
  );
};

export default function Rechnungsbuecher() {
  const [tab, setTab] = useState("ausgang");
  const knopf = (k, label) => (
    <button onClick={() => setTab(k)} data-testid={`tab-${k}`}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>{label}</button>
  );
  return (
    <div className="p-4 sm:p-8 max-w-7xl" data-testid="rechnungsbuecher-page">
      <h1 className="text-3xl font-semibold text-stone-900">Ausgangsbuch &amp; Belegjournal</h1>
      <p className="text-sm text-stone-600 mt-1 mb-6">Lückenlose Übersicht aller Rechnungen und Buchungsbelege – zum Ausdrucken oder als Datei für Steuerberatung und Finanzamt.</p>
      <div className="flex gap-2 border-b border-stone-200 mb-6">{knopf("ausgang", "Rechnungsausgangsbuch")}{knopf("journal", "Belegjournal")}</div>
      {tab === "ausgang" ? <Ausgangsbuch /> : <Belegjournal />}
    </div>
  );
}
